import { useEffect } from 'react';
import {
  MARKETING_TITLE,
  MARKETING_CANONICAL,
  MARKETING_META_TAGS,
  MARKETING_STRUCTURED_DATA,
} from './marketingHeadData';

/**
 * Phase 14.8 — Lightweight Head-Manager für die Marketing-Page.
 *
 * Vermeidet eine zusätzliche Dependency (react-helmet) und setzt stattdessen
 * direkt {@code document.title} sowie ein paar Meta-Tags. Vorhandene Tags aus
 * {@code index.html} werden bei Bedarf aktualisiert; fehlende werden injiziert
 * und beim Unmount wieder entfernt.
 */
export function MarketingHead() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const prevTitle = document.title;
    document.title = MARKETING_TITLE;

    const ownedNodes: HTMLElement[] = [];

    function ensureMeta(spec: (typeof MARKETING_META_TAGS)[number]) {
      const selector = `meta[${spec.attr}="${spec.key}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      const owned = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(spec.attr, spec.key);
        document.head.appendChild(el);
      }
      const prevContent = el.getAttribute('content');
      el.setAttribute('content', spec.value);
      if (owned) ownedNodes.push(el);
      else el.dataset.prevContent = prevContent ?? '';
    }

    MARKETING_META_TAGS.forEach(ensureMeta);

    // Canonical link
    const existingCanonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    let canonical = existingCanonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
      ownedNodes.push(canonical);
    }
    canonical.href = MARKETING_CANONICAL;

    // JSON-LD
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.dataset.testid = 'dispatch24-jsonld';
    ld.textContent = JSON.stringify(MARKETING_STRUCTURED_DATA);
    document.head.appendChild(ld);
    ownedNodes.push(ld);

    return () => {
      document.title = prevTitle;
      for (const node of ownedNodes) {
        node.parentNode?.removeChild(node);
      }
      MARKETING_META_TAGS.forEach((spec) => {
        const selector = `meta[${spec.attr}="${spec.key}"]`;
        const el = document.head.querySelector<HTMLMetaElement>(selector);
        if (!el) return;
        if (el.dataset.prevContent !== undefined) {
          el.setAttribute('content', el.dataset.prevContent);
          delete el.dataset.prevContent;
        }
      });
    };
  }, []);

  return null;
}
